/// <amd-module name="VSS/Features/Charts/Internal/ColorUtilities" />

import * as Charts_Resources from "VSS/Features/Charts/Resources";

export class ColorUtilities {
    /**
     * Relative luminace as defined here:
     * https://www.w3.org/TR/WCAG20/#relativeluminancedef
     */
    private static getRelativeLuminance(rgbArray: number[]): number {
        const relativeColorArray = rgbArray.map(color => {
            color = color / 255;
            color = color < 0.03928 ? color / 12.92 : Math.pow((color + 0.055) / 1.055, 2.4);

            return color;
        });

        return 0.2126 * relativeColorArray[0] + 0.7152 * relativeColorArray[1] + 0.0722 * relativeColorArray[2];
    }

    /**
     * Contrast ratio as defined here
     * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
     */
    private static getContrastRatio(rgbArray: number[]): number {
        const l1 = ColorUtilities.getRelativeLuminance([255, 255, 255]);
        const l2 = ColorUtilities.getRelativeLuminance(rgbArray);
        return (l1 + 0.05) / (l2 + 0.05);
    }

    private static toRgbArray(color: string): number[] {
        // converts colors of the form rgba(r,g,b,a) to an array. This format is usually followed by the theme service.
        if (color.startsWith("rgba")) {
            const splitColorArray = color
                .substring(5, color.length - 1)
                .replace(/ /g, "")
                .split(",");

            return [parseInt(splitColorArray[0]), parseInt(splitColorArray[1]), parseInt(splitColorArray[2])];
        } else {
            const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
            const colorInString = color.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(colorInString);
            if (result === null) {
                throw Charts_Resources.ChartColorValidationErrorMessage;
            }
            const rgbArray: number[] = result.splice(1, result.length - 1).map(color => {
                return parseInt(color, 16);
            });
            return rgbArray;
        }
    }
    /**
     * Select between black or white Foreground Color as a complement to the supplied background color.
     *
     * HighCharts implements this feature by allowing users to set the dataLabels color to "contrast". However their algorithm
     * changed between v4 and v5 (https://forum.highcharts.com/highcharts-usage/contrast-of-labels-on-colored-background-t37511/).
     * So we use this utility function using our own algorithm to determine whether or not the foreground color should be black
     * or white.
     *
     * @param {string} background - Hex color
     * @returns {string} Hex color of White or Black
     */
    public static selectForegroundColor(backgroundColor: string): string {
        return ColorUtilities.isDark(backgroundColor) ? "#FFFFFF" : "#000000";
    }

    /**
     * Indicates if the specified color is dark.
     *
     * Dark colors need to be paired with white in order to ensure accessible color contrast ratios.
     *
     * Similarly, light colors need to be paired with black.
     */
    public static isDark(color: string) {
        const rgbArray = ColorUtilities.toRgbArray(color);
        const contrastRatio = ColorUtilities.getContrastRatio(rgbArray);
        return contrastRatio > 4.5;
    }
}
